import { useEffect, useState } from 'react';
import axios from 'axios';
import env from 'react-dotenv';
import { useAuthHeader, useAuthUser } from 'react-auth-kit';
import {
    Badge, Button, Checkbox, DataTable, FormDrawer, FormSection, IconButton, Input,
    Modal, Select, Spin, TableStatusBar, useAlert,
} from '../ui';
import { DataTableColumn } from '../ui/DataTable';
import User from '../../Models/User';
import datas from '../../Assets/data.json';
import { Crypter } from '../../Functions/Security/Crypter';
import {
    AlertTriangle, Ban, History, KeyRound, MonitorDot, Pencil, RefreshCw, Tv, Volume2, VolumeX, WifiOff,
} from 'lucide-react';

/**
 * Supervision des écrans LiveScreem enrôlés — POST /api/Ecran/Admin (droit D17).
 * Options : LIST | VALIDATE | MODIFIER | RECHARGER | REVOQUER | JOURNAL.
 * Un agent non administrateur ne gère que les écrans de son agence (serveur).
 */

const REFRESH_EVERY = 60 * 1000;

function postAdmin(authHeader: () => string, payload: any): Promise<any> {
    return axios.request({
        method: 'post', maxBodyLength: Infinity,
        url: env.API_BASE_URL + datas.API_LINKs.Ecran.Admin,
        headers: { 'Content-Type': 'application/json', 'Authorization': authHeader() },
        data: Crypter.encrypt(JSON.stringify(payload)),
    }).then((res) => JSON.parse(Crypter.decrypt(res.data)).Value);
}

function pick(row: any, ...keys: string[]) {
    if (!row) return undefined;
    for (const k of keys) if (row[k] != null && row[k] !== '') return row[k];
    return undefined;
}

function ilYA(value: any): string {
    if (!value) return '—'
    const d = new Date(value)
    if (isNaN(d.getTime())) return String(value)
    const s = Math.max(0, Math.floor((Date.now() - d.getTime()) / 1000))
    if (s < 60) return `il y a ${s} s`
    const m = Math.floor(s / 60)
    if (m < 60) return `il y a ${m} min`
    const h = Math.floor(m / 60)
    if (h < 24) return `il y a ${h} h`
    return d.toLocaleDateString('fr-FR')
}

const ETAT_META: Record<string, { color: 'green' | 'red' | 'gray'; dot: string; label: string }> = {
    EN_LIGNE: { color: 'green', dot: 'bg-emerald-500', label: 'En ligne' },
    HORS_LIGNE: { color: 'red', dot: 'bg-red-500', label: 'Hors ligne' },
    REVOQUE: { color: 'gray', dot: 'bg-gray-400', label: 'Révoqué' },
};

export default function GestionEcrans() {
    const authHeader = useAuthHeader()
    const auth = useAuthUser()
    const user: User = JSON.parse(JSON.stringify(auth() || {}))
    const alert = useAlert()

    const isAdmin = ['SUADMIN', 'ADMIN', 'IT'].includes(user.RoleId)
    const habilitations: any[] = (() => {
        try { return JSON.parse(Crypter.decrypt(localStorage.getItem(datas.Habilitations) || '')) || [] } catch { return [] }
    })()
    const canEdit = isAdmin || habilitations.some((h: any) => h.CodeHabi === 'D17')

    const [rows, setRows] = useState<any[]>([])
    const [loading, setLoading] = useState(false)
    const [saving, setSaving] = useState(false)
    const [filiales, setFiliales] = useState<any[]>([])
    const [services, setServices] = useState<any[]>([])
    const [filtreFiliale, setFiltreFiliale] = useState(isAdmin ? '' : (user.CodeFiliale || ''))

    // Drawer enrôlement / modification
    const [drawer, setDrawer] = useState<null | { mode: 'VALIDATE' | 'MODIFIER'; record?: any }>(null)
    const [fCode, setFCode] = useState('')
    const [fFiliale, setFFiliale] = useState('')
    const [fServices, setFServices] = useState<string[]>([])
    const [fLibelle, setFLibelle] = useState('')
    const [fVoix, setFVoix] = useState(true)

    // Journal
    const [journal, setJournal] = useState<{ ecran: any; lignes: any[]; loading: boolean } | null>(null)

    // Confirmation révocation / rechargement
    const [confirm, setConfirm] = useState<null | { option: 'REVOQUER' | 'RECHARGER' | 'COUPER_VOIX'; record: any }>(null)

    useEffect(() => {
        LoadEcrans()
        LoadReferentiels()
        const t = setInterval(LoadEcrans, REFRESH_EVERY) // auto-refresh 60 s
        return () => clearInterval(t)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filtreFiliale])

    function LoadEcrans() {
        setLoading(true)
        const payload: any = { Option: 'LIST' }
        if (filtreFiliale) payload.CodeFiliale = filtreFiliale
        postAdmin(authHeader, payload)
            .then((v: any) => {
                if (v.StatutCode == 200) {
                    setRows((Array.isArray(v.Result) ? v.Result : []).map((e: any, i: number) => ({ id: e.EcranId ?? i, ...e })))
                } else { setRows([]); alert.warning(v.Message) }
            })
            .catch((e: any) => { setRows([]); alert.error('Error', e) })
            .finally(() => setLoading(false))
    }

    function LoadReferentiels() {
        axios.get(env.API_BASE_URL + datas.API_LINKs.GetAllFiliales + Crypter.encrypt(user.CodeAbonne),
            { headers: { 'Accept': 'text/plain', 'Authorization': authHeader() } })
            .then((res) => {
                const v = JSON.parse(Crypter.decrypt(res.data)).Value
                if (v.StatutCode == 200) setFiliales(Array.isArray(v.Result) ? v.Result : [])
            }).catch(() => { })
        axios.get(env.API_BASE_URL + datas.API_LINKs.GetFilialeServices + Crypter.encrypt(user.CodeAbonne),
            { headers: { 'Accept': 'text/plain', 'Authorization': authHeader() } })
            .then((res) => {
                const v = JSON.parse(Crypter.decrypt(res.data)).Value
                if (v.StatutCode == 200) {
                    const svc = Array.isArray(v.Result?.[1]) ? v.Result[1] : (v.Result || [])
                    setServices(svc)
                }
            }).catch(() => { })
    }

    /* ---------- Enrôlement / modification ---------- */

    const filialesOptions = (isAdmin ? filiales : filiales.filter((f: any) => f.CodeFiliale === user.CodeFiliale))
        .map((f: any) => ({ value: f.CodeFiliale, label: `${f.CodeFiliale} — ${f.Libelle || ''}` }))
    const servicesFiliale = services.filter((s: any) => !fFiliale || s.CodeFiliale == fFiliale)

    function openEnrolement() {
        setFCode(''); setFFiliale(isAdmin ? '' : (user.CodeFiliale || '')); setFServices([]); setFLibelle('')
        setFVoix(true)                                   // « Voix active » cochée par défaut
        setDrawer({ mode: 'VALIDATE' })
    }

    function openModifier(record: any) {
        setFCode('')
        setFFiliale(record.CodeFiliale || '')
        setFServices(String(record.Services || '').split(',').map((s: string) => s.trim()).filter(Boolean))
        setFLibelle(record.Libelle || '')
        setFVoix(record.VoixActive !== false)
        setDrawer({ mode: 'MODIFIER', record })
    }

    function toggleService(codeS: string, checked: boolean) {
        setFServices(prev => checked ? [...prev, codeS] : prev.filter(s => s !== codeS))
    }

    function handleSave() {
        const payload: any = drawer?.mode === 'VALIDATE'
            ? { Option: 'VALIDATE', PairingCode: fCode.trim().toUpperCase() }
            : { Option: 'MODIFIER', EcranId: drawer?.record?.EcranId }
        if (drawer?.mode === 'VALIDATE' && !/^[A-Z0-9]{6}$/.test(payload.PairingCode)) {
            alert.warning('Code d\'enrôlement invalide — 6 caractères affichés sur la télévision.')
            return
        }
        if (!fFiliale) { alert.warning('Choisissez l\'agence de l\'écran.'); return }
        payload.CodeFiliale = fFiliale
        // Services : tableau ["GS1","GS2"] ; vide = tous les services de l'agence
        payload.Services = fServices
        if (fLibelle.trim()) payload.Libelle = fLibelle.trim()
        payload.VoixActive = fVoix

        setSaving(true)
        postAdmin(authHeader, payload)
            .then((v: any) => {
                if (v.StatutCode == 200) {
                    alert.success(drawer?.mode === 'VALIDATE'
                        ? 'Écran enrôlé : la télévision affiche la file dans quelques secondes'
                        : (v.Message || 'Écran modifié'))
                    setDrawer(null)
                    LoadEcrans()
                } else alert.warning(v.Message)
            })
            .catch((e: any) => alert.error('Error', e))
            .finally(() => setSaving(false))
    }

    /* ---------- Actions par ligne ---------- */

    function executeAction() {
        const a = confirm
        if (!a) return
        setSaving(true)
        const payload = a.option === 'COUPER_VOIX'
            ? { Option: 'MODIFIER', EcranId: a.record.EcranId, VoixActive: false }
            : { Option: a.option, EcranId: a.record.EcranId }
        postAdmin(authHeader, payload)
            .then((v: any) => {
                if (v.StatutCode == 200) { alert.success(v.Message || (a.option === 'COUPER_VOIX' ? 'Voix coupée' : 'OK')); setConfirm(null); LoadEcrans() }
                else alert.warning(v.Message)
            })
            .catch((e: any) => alert.error('Error', e))
            .finally(() => setSaving(false))
    }

    function openJournal(record: any) {
        setJournal({ ecran: record, lignes: [], loading: true })
        postAdmin(authHeader, { Option: 'JOURNAL', EcranId: record.EcranId })
            .then((v: any) => {
                if (v.StatutCode == 200) setJournal({ ecran: record, lignes: v.Result || [], loading: false })
                else { alert.warning(v.Message); setJournal(null) }
            })
            .catch((e: any) => { alert.error('Error', e); setJournal(null) })
    }

    const nbHorsLigne = rows.filter((r: any) => r.Etat === 'HORS_LIGNE').length
    const conflits = rows.filter((r: any) => r.ConflitVoix === true && r.Etat !== 'REVOQUE')

    const columns: DataTableColumn[] = [
        {
            field: 'Etat', headerName: 'État', width: 110,
            renderCell: ({ value }) => {
                const m = ETAT_META[value] || { color: 'gray' as const, dot: 'bg-gray-400', label: value || '—' }
                return (
                    <span className="inline-flex items-center gap-2">
                        <span className={`w-2.5 h-2.5 rounded-full ${m.dot}`} />
                        <Badge color={m.color}>{m.label}</Badge>
                    </span>
                )
            },
        },
        {
            field: 'Libelle', headerName: 'Écran', width: 180,
            renderCell: ({ row }) => (
                <span className="inline-flex items-center gap-2 font-medium">
                    <Tv size={15} className="text-[var(--eb-primary)]" /> {row.Libelle || `Écran #${row.EcranId}`}
                </span>
            ),
        },
        {
            field: 'LibelleFiliale', headerName: 'Agence', width: 160,
            renderCell: ({ row }) => <span>{row.LibelleFiliale || row.CodeFiliale || '—'}</span>,
        },
        {
            field: 'Services', headerName: 'Services', width: 120,
            renderCell: ({ value }) => value
                ? <span className="font-mono text-xs">{value}</span>
                : <span className="text-xs text-[var(--eb-text-muted)]">Tous</span>,
        },
        {
            field: 'VoixActive', headerName: 'Voix', width: 200,
            renderCell: ({ row }) => (
                <div className="flex flex-col gap-1 py-1">
                    {row.VoixActive === false
                        ? <span className="inline-flex items-center gap-1.5"><VolumeX size={14} className="text-[var(--eb-text-muted)]" /><Badge color="gray">Coupée</Badge></span>
                        : <span className="inline-flex items-center gap-1.5"><Volume2 size={14} className="text-emerald-600" /><Badge color="green">Active</Badge></span>}
                    {row.ConflitVoix === true && (
                        <span className="inline-flex items-start gap-1 text-[11px] leading-tight text-amber-700 dark:text-amber-300">
                            <AlertTriangle size={12} className="mt-px shrink-0" />
                            Risque d'écho avec : {row.EcransMemeVoix || '—'}
                        </span>
                    )}
                </div>
            ),
        },
        {
            field: 'DernierSignal', headerName: 'Dernier signal', width: 120,
            renderCell: ({ value }) => <span className="text-xs">{ilYA(value)}</span>,
        },
        { field: 'VersionInstall', headerName: 'Version', width: 80, renderCell: ({ value }) => <span className="font-mono text-xs">{value || '—'}</span> },
        { field: 'AdresseIp', headerName: 'Adresse IP', width: 110, renderCell: ({ value }) => <span className="font-mono text-xs">{value || '—'}</span> },
        ...(canEdit ? [{
            field: 'actions', headerName: 'Actions', width: 200, sortable: false,
            renderCell: ({ row }: any) => row.Etat !== 'REVOQUE' && (
                <div className="flex items-center gap-1">
                    <IconButton tooltip="Modifier (agence, services, nom, voix)" color="primary" size={28}
                        onClick={(e: any) => { e.stopPropagation(); openModifier(row) }}>
                        <Pencil size={14} />
                    </IconButton>
                    {row.VoixActive !== false && (
                        <IconButton tooltip="Couper la voix" color="default" size={28}
                            onClick={(e: any) => { e.stopPropagation(); setConfirm({ option: 'COUPER_VOIX', record: row }) }}>
                            <VolumeX size={14} />
                        </IconButton>
                    )}
                    <IconButton tooltip="Recharger l'écran" color="default" size={28}
                        onClick={(e: any) => { e.stopPropagation(); setConfirm({ option: 'RECHARGER', record: row }) }}>
                        <RefreshCw size={14} />
                    </IconButton>
                    <IconButton tooltip="Journal" color="default" size={28}
                        onClick={(e: any) => { e.stopPropagation(); openJournal(row) }}>
                        <History size={14} />
                    </IconButton>
                    <IconButton tooltip="Révoquer — retour à l'enrôlement" color="danger" size={28}
                        onClick={(e: any) => { e.stopPropagation(); setConfirm({ option: 'REVOQUER', record: row }) }}>
                        <Ban size={14} />
                    </IconButton>
                </div>
            ),
        } as DataTableColumn] : [{
            field: 'journal', headerName: '', width: 60, sortable: false,
            renderCell: ({ row }: any) => (
                <IconButton tooltip="Journal" color="default" size={28}
                    onClick={(e: any) => { e.stopPropagation(); openJournal(row) }}>
                    <History size={14} />
                </IconButton>
            ),
        } as DataTableColumn]),
    ]

    return (
        <div>
            {nbHorsLigne > 0 && (
                <div className="mb-3 flex items-center gap-2 px-3 py-2.5 rounded-lg border border-red-300 bg-red-50 dark:bg-red-900/20 text-sm text-red-700 dark:text-red-300">
                    <WifiOff size={16} />
                    {nbHorsLigne} écran{nbHorsLigne > 1 ? 's' : ''} hors ligne — aucun signal reçu récemment.
                </div>
            )}
            {conflits.length > 0 && (
                <div className="mb-3 flex items-start gap-2 px-3 py-2.5 rounded-lg border border-amber-300 bg-amber-50 dark:bg-amber-900/20 text-sm text-amber-700 dark:text-amber-300">
                    <AlertTriangle size={16} className="mt-0.5 shrink-0" />
                    <div>
                        {conflits.map((r: any) => (
                            <div key={r.EcranId}>
                                <b>{r.Libelle || `Écran #${r.EcranId}`}</b> — Risque d'écho avec : {r.EcransMemeVoix || '—'}
                            </div>
                        ))}
                        <div className="text-xs opacity-80 mt-0.5">À vérifier sur place : un seul écran par salle doit parler.</div>
                    </div>
                </div>
            )}
            {!canEdit && (
                <div className="mb-3 px-3 py-2 rounded-lg border border-amber-300 bg-amber-50 dark:bg-amber-900/20 text-xs text-amber-700 dark:text-amber-300">
                    Lecture seule — le droit D17 est requis pour gérer les écrans.
                </div>
            )}

            <div className="eb-table-wrap">
                <div className="mb-3 flex items-end justify-between gap-3 flex-wrap">
                    {isAdmin && (
                        <div className="max-w-[260px] flex-1">
                            <Select
                                label="Filiale"
                                value={filtreFiliale}
                                onChange={setFiltreFiliale}
                                options={[
                                    { value: '', label: 'Toutes les filiales' },
                                    ...filialesOptions,
                                ]}
                            />
                        </div>
                    )}
                    {canEdit && (
                        <Button variant="primary" size="sm" startIcon={<KeyRound size={16} />} onClick={openEnrolement}>
                            Enrôler un écran
                        </Button>
                    )}
                </div>
                <Spin spinning={loading}>
                    <DataTable rows={rows} columns={columns} height={420} searchable emptyText="Aucun écran enrôlé" />
                </Spin>
            </div>
            <TableStatusBar count={rows.length} loading={loading} onRefresh={LoadEcrans} />

            {/* Drawer : enrôlement / modification */}
            <FormDrawer
                open={!!drawer}
                onClose={() => setDrawer(null)}
                title={drawer?.mode === 'VALIDATE' ? 'Enrôler un écran' : `Modifier — ${drawer?.record?.Libelle || `Écran #${drawer?.record?.EcranId}`}`}
                hint={drawer?.mode === 'VALIDATE'
                    ? 'Code à 6 caractères affiché sur la télévision. La file s\'affichera dans quelques secondes.'
                    : 'La télévision applique les changements sans intervention sur place.'}
                footer={
                    <>
                        <Button variant="outline" onClick={() => setDrawer(null)}>Annuler</Button>
                        <Button variant="primary" onClick={handleSave} loading={saving} startIcon={drawer?.mode === 'VALIDATE' ? <KeyRound size={15} /> : undefined}>
                            {drawer?.mode === 'VALIDATE' ? 'Enrôler' : 'Enregistrer'}
                        </Button>
                    </>
                }
            >
                <FormSection title={drawer?.mode === 'VALIDATE' ? 'Code d\'enrôlement' : 'Écran'}>
                    {drawer?.mode === 'VALIDATE' && (
                        <Input
                            label="Code affiché sur la télévision *"
                            value={fCode}
                            onChange={(e: any) => setFCode(e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 6))}
                            placeholder="ABC123"
                            required
                        />
                    )}
                    <Select
                        label="Agence *"
                        value={fFiliale}
                        onChange={(v: string) => { setFFiliale(v); setFServices([]) }}
                        options={[{ value: '', label: '—' }, ...filialesOptions]}
                    />
                    <Input
                        label="Nom de l'écran"
                        value={fLibelle}
                        placeholder="Ex. Écran hall Akwa"
                        onChange={(e: any) => setFLibelle(e.target.value)}
                    />
                    <Checkbox
                        checked={fVoix}
                        onChange={setFVoix}
                        label={<span className="text-sm">Voix active <span className="text-xs text-[var(--eb-text-muted)]">— un seul écran par salle doit parler</span></span>}
                    />
                </FormSection>
                <FormSection
                    title="Services affichés"
                    subtitle={servicesFiliale.length ? 'Dans l\'ordre d\'affichage voulu — aucune case cochée = tous les services.' : 'Aucun service dans cette agence.'}
                >
                    <div className="divide-y divide-[var(--eb-border)] rounded-lg border border-[var(--eb-border)]">
                        {servicesFiliale.map((s: any) => (
                            <label key={s.CodeS} className="flex items-center gap-3 px-3 py-2.5 cursor-pointer">
                                <Checkbox
                                    checked={fServices.includes(s.CodeS)}
                                    onChange={(v: boolean) => toggleService(s.CodeS, v)}
                                />
                                <span className="text-sm font-medium text-[var(--eb-text)]">{s.CodeS}</span>
                                <span className="text-xs text-[var(--eb-text-muted)]">{s.Libelle}</span>
                            </label>
                        ))}
                    </div>
                </FormSection>
            </FormDrawer>

            {/* Drawer journal */}
            <FormDrawer
                open={!!journal}
                onClose={() => setJournal(null)}
                title={`Journal — ${journal?.ecran?.Libelle || `Écran #${journal?.ecran?.EcranId}`}`}
            >
                <Spin spinning={journal?.loading ?? false}>
                    <div className="divide-y divide-[var(--eb-border)] rounded-lg border border-[var(--eb-border)]">
                        {(journal?.lignes || []).length === 0 && !journal?.loading && (
                            <div className="px-3 py-4 text-center text-xs text-[var(--eb-text-muted)]">Aucun événement</div>
                        )}
                        {(journal?.lignes || []).map((j: any, i: number) => (
                            <div key={i} className="px-3 py-2.5">
                                <div className="flex items-center justify-between gap-2">
                                    <Badge color={j.Evenement === 'REVOQUE' ? 'red' : j.Evenement === 'ENROLE' ? 'green' : 'blue'}>
                                        {j.Evenement}
                                    </Badge>
                                    <span className="text-xs text-[var(--eb-text-muted)]">{ilYA(j.DateEvenement)}</span>
                                </div>
                                {j.Detail && <p className="text-xs text-[var(--eb-text)] mt-1">{j.Detail}</p>}
                                {j.Auteur && <p className="text-[11px] text-[var(--eb-text-muted)]">par {j.Auteur}</p>}
                            </div>
                        ))}
                    </div>
                </Spin>
            </FormDrawer>

            {/* Confirmation rechargement / révocation */}
            <Modal
                open={!!confirm}
                onClose={() => setConfirm(null)}
                title={confirm?.option === 'REVOQUER' ? 'Révoquer l\'écran' : confirm?.option === 'COUPER_VOIX' ? 'Couper la voix' : 'Recharger l\'écran'}
                footer={
                    <>
                        <Button variant="outline" onClick={() => setConfirm(null)}>Annuler</Button>
                        <Button
                            variant={confirm?.option === 'REVOQUER' ? 'danger' : 'primary'}
                            loading={saving}
                            onClick={executeAction}
                            startIcon={confirm?.option === 'REVOQUER' ? <Ban size={15} /> : confirm?.option === 'COUPER_VOIX' ? <VolumeX size={15} /> : <RefreshCw size={15} />}
                        >
                            {confirm?.option === 'REVOQUER' ? 'Révoquer' : confirm?.option === 'COUPER_VOIX' ? 'Couper la voix' : 'Recharger'}
                        </Button>
                    </>
                }
            >
                <p className="text-sm text-[var(--eb-text)]">
                    {confirm?.option === 'REVOQUER'
                        ? <>La télévision <b>{confirm?.record?.Libelle || `#${confirm?.record?.EcranId}`}</b> reviendra à l'écran d'enrôlement et devra être validée à nouveau.</>
                        : confirm?.option === 'COUPER_VOIX'
                            ? <>La télévision <b>{confirm?.record?.Libelle || `#${confirm?.record?.EcranId}`}</b> continuera d'afficher les appels, sans les lire à voix haute.</>
                            : <>La télévision <b>{confirm?.record?.Libelle || `#${confirm?.record?.EcranId}`}</b> va se recharger immédiatement.</>}
                </p>
            </Modal>
        </div>
    );
}
